<template>
  <HeadComponent>
    <html lang="en" prefix="og: http://ogp.me/ns#" />
    <meta charset="utf-8" name="format-detection" co12ntent="telephone=no" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <link rel="icon" href="/report/favicon.ico" type="image/x-icon" />
    <meta v-if="root.meta.isPrivate" name="robots" content="noindex" />
    <meta name="description" :content="description" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta
      name="keywords"
      content="interactive sandbox, free malware sandbox, sandbox malware online, sandbox online, sandbox service, sandbox analysis online, malware sandbox, malware sandbox online, malware analisys online, malware sandbox analysis, malware hunting, malware sandboxes services, online sandbox, online malware sandbox, online sandbox analysis"
    />
    <meta name="msvalidate.01" content="85B82A2B600D59A16B22BC3D08EE7ABB" />
    <title>{{ title }}</title>

    <link rel="canonical" :href="canonicalHref" />

    <!-- Google Tag Manager -->
    <component :is="'script'">
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new
      Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-NSC8CSS');
    </component>

    <!-- Youtube blocker -->
    <component
      :is="'script'"
      type="text/plain"
      data-consent="consent"
      data-category="functional marketing"
      inversed
    >
      !function(){let e=/loaded|complete|interactive/.test(document.readyState);if(e)return t();function t(){let e=document.querySelectorAll('iframe');for(let t of e){let n=t.getAttribute('src')||'',o=n.indexOf('youtube.com')>-1;if(!o)continue;let r=t.parentNode,a=document.createElement('p');a.textContent='There may be a YouTube video here. Unfortunately, functional and marketing cookies have not been accepted yet',t.remove(),r.append(a),r.style='display: flex;justify-content: center;align-items: center;text-align: center;',a.style='font-size: 13px;color: #848484;'}}document.addEventListener('DOMContentLoaded',t)}();
    </component>

    <!-- Open Graph data -->
    <meta property="og:title" :content="title" />
    <meta property="og:url" :content="url" />
    <meta property="og:type" content="product" />
    <meta property="og:description" :content="description" />
    <meta property="og:image" content="https://any.run/img/anyrun-logo.png" />
    <meta property="og:locale" content="en_US" />

    <!-- Twitter Card -->
    <meta name="twitter:card" content="summary" />
    <meta name="twitter:title" :content="title" />
    <meta name="twitter:description" :content="description" />
    <meta name="twitter:url" :content="url" />
    <meta name="twitter:image" content="https://any.run/img/logo-anyrun.png" />

    <component :is="'script'" v-for="tag in ldTags" type="application/ld+json">
      {{ tag }}
    </component>
  </HeadComponent>
  <!-- Google Tag Manager (noscript) -->
  <div v-if="enableTrackers" v-html="googleIframe" />
  <!-- End Google Tag Manager (noscript) -->
</template>
<script>
import { defineComponent } from 'vue';
import { map, get } from 'lodash';
import { Head as HeadComponent } from '@vueuse/head';

const googleIframe = `<noscript>
  <iframe
    src="https://www.googletagmanager.com/ns.html?id=GTM-NSC8CSS"
    height="0"
    width="0"
    :style="{ display: 'none', visibility: 'hidden' }"
  />
</noscript>`;

export default defineComponent({
  components: { HeadComponent },
  inject: ['root', 'printRef'],
  data: () => ({ googleIframe, isTrackersLoadAllowed: false }),
  computed: {
    enableTrackers() {
      return this.isTrackersLoadAllowed && this.root.vue_isTrackersScriptsEnabled;
    },
    url() {
      return `https://report.any.run/${this.root.meta.sha256}/${this.root.meta.uuid}`;
    },
    canonicalHref() {
      return `https://any.run/report/${this.root.meta.sha256}/${this.root.meta.uuid}`;
    },
    contentUrl() {
      return `https://content.any.run/tasks/${this.root.meta.uuid}`;
    },
    tags() {
      if (this.root.meta.tags.length) {
        return `tagged as ${this.root.meta.tags.join(', ')}`;
      }

      // default value
      return null;
    },
    description() {
      const result = [`Online sandbox report for ${this.root.meta.taskName}`];
      if (this.tags) result.push(this.tags);

      if (this.root.general.verdict) {
        result.push(`verdict: ${this.root.general.verdict.value}`);
      }

      return result.join(', ');
    },
    title() {
      const objectName = this.root.general.mainObject.value;
      const verdict = this.root.general.verdict ? this.root.general.verdict.value : '';

      return `Malware analysis ${objectName} ${verdict} | ANY.RUN - Malware Sandbox Online`;
    },
    ldTags() {
      const menu = [
        'General',
        'Behavior',
        'MalConf',
        'Static information',
        'Screenshots',
        'Process',
        'Registry activity',
        'Files activity',
        'Synchronization',
        'RPC Requests',
        'Debug',
        'Network',
      ];

      const result = [
        {
          '@context': 'http://schema.org',
          '@type': 'WebPage',
          description: this.description,
          headline: 'ANY.RUN - Interactive Online Malware Sandbox',
          image: {
            '@type': 'ImageObject',
            url: 'https://any.run/img/logo.svg',
          },
          inLanguage: 'en-US',
          mainEntityOfPage: this.url,
          name: 'ANY.RUN',
          url: this.url,
        },
        {
          '@context': 'http://schema.org/',
          '@type': 'WPHeader',
          headline: 'ANY.RUN - Interactive Online Malware Sandbox',
          description: this.description,
          inLanguage: 'en-US',
          name: 'ANY.RUN',
          url: this.url,
        },
        {
          '@context': 'http://schema.org',
          '@type': 'Organization',
          '@id': 'https://any.run/#organization',
          name: 'ANY.RUN',
          legalName: 'ANY.RUN LLC',
          url: 'https://any.run',
          logo: 'https://any.run/img/logo.svg',
          email: 'support@any.run',
          sameAs: [
            'https://www.facebook.com/www.any.run',
            'https://twitter.com/anyrun_app',
            'https://www.youtube.com/channel/UCOgCPho7lzmH7m6fPNlukrQ',
          ],
          contactPoint: [
            {
              '@type': 'ContactPoint',
              contactType: 'customer service',
              url: 'https://app.any.run/contact-us',
              email: 'support@any.run',
            },
          ],
        },
      ];

      // As video is Array we have to get first item and if it's null then there is no video
      const hasVideo = get(this.root, 'videoAndScreenshots.video[0]', false);

      if (hasVideo) {
        const videoName = [`Video review for ${this.root.meta.taskName}`];
        if (this.tags) videoName.push(this.tags);

        result.push({
          '@context': 'https://schema.org',
          '@type': 'VideoObject',
          name: videoName.join(', '),
          description:
            'The video created by the ANY.RUN malware hunting service allows us to see the incident as it unfolds',
          thumbnailUrl: `${this.contentUrl}/download/mp4`,
          uploadDate: this.root.general.analysisDate,
          contentUrl: `${this.contentUrl}/download/mp4`,
        });

        menu.push('Video');
      }

      result.push(
        {
          '@context': 'https://schema.org',
          '@graph': map(menu, (name) => ({
            '@context': 'https://schema.org',
            '@type': 'SiteNavigationElement',
            name,
            url: `${this.url}/#${name}`,
          })),
        },
        {
          '@context': 'http://schema.org/',
          '@type': 'WPFooter',
          image: {
            '@type': 'ImageObject',
            url: 'https://any.run/img/logo.svg',
          },
          inLanguage: 'en-US',
          name: 'ANY.RUN',
          url: this.url,
        }
      );

      return result;
    },
  },
  methods: {
    startTrackersLoading() {
      this.isTrackersLoadAllowed = true;
    },
  },
  mounted() {
    window.addEventListener('load', this.startTrackersLoading);
  },
  beforeUnmount() {
    window.removeEventListener('load', this.startTrackersLoading);
  },
});
</script>
